/**
 * Pure helpers for the calendar date slicer. Compute concrete [start, endExcl]
 * bounds for a fixed period pick (month / quarter / year), fiscal-aware for
 * quarter & year. Months are always calendar. `endExcl` is exclusive.
 */

function pad(n) { return String(n).padStart(2, '0'); }
function iso(y, m, d) { return `${y}-${pad(m)}-${pad(d)}`; }

// Add `n` months to (year, monthIndex1-12) → first of that month.
function addMonths(year, month1, n) {
  const zero = (year * 12) + (month1 - 1) + n;
  return { year: Math.floor(zero / 12), month1: (zero % 12) + 1 };
}

export function isDateField(field) {
  if (!field) return false;
  const dt = String(field.data_type || '').toLowerCase();
  if (['date', 'datetime', 'timestamp', 'datetimetz'].includes(dt)) return true;
  // Name heuristic as a fallback (mirrors the model's date detection).
  return /(^|_)date(_|$)|_at$|_ts$/.test(String(field.name || '').toLowerCase());
}

// Bounds for a calendar month token 'YYYY-MM'.
export function monthBounds(year, month1) {
  const start = iso(year, month1, 1);
  const nxt = addMonths(year, month1, 1);
  return { start, endExcl: iso(nxt.year, nxt.month1, 1) };
}

// Fiscal-aware quarter bounds. `q` is 1-4 within the fiscal year that STARTS in
// calendar year `fyStartYear` at month `fsm`.
export function quarterBounds(fyStartYear, q, fsm = 1) {
  const s = addMonths(fyStartYear, fsm, (q - 1) * 3);
  const e = addMonths(s.year, s.month1, 3);
  return { start: iso(s.year, s.month1, 1), endExcl: iso(e.year, e.month1, 1) };
}

// Fiscal-aware year bounds for the fiscal year starting in `fyStartYear`.
export function yearBounds(fyStartYear, fsm = 1) {
  const s = { year: fyStartYear, month1: fsm };
  const e = addMonths(s.year, s.month1, 12);
  return { start: iso(s.year, s.month1, 1), endExcl: iso(e.year, e.month1, 1) };
}

export function periodLabel(grain, year, n, fsm = 1) {
  const fiscal = (Number(fsm) || 1) !== 1;
  const yLabel = fiscal ? `FY${year}` : `${year}`;
  if (grain === 'month') {
    const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${names[n - 1]} ${year}`;
  }
  if (grain === 'quarter') return `${yLabel} Q${n}`;
  return yLabel;
}

// Build a period entry {token, label, start, endExcl} for a pick.
export function makePeriod(grain, year, n, fsm = 1) {
  let b, token;
  if (grain === 'month') { b = monthBounds(year, n); token = `${year}-${pad(n)}`; }
  else if (grain === 'quarter') { b = quarterBounds(year, n, fsm); token = `${year}-Q${n}`; }
  else { b = yearBounds(year, fsm); token = `${year}`; }
  return { token, label: periodLabel(grain, year, n, fsm), start: b.start, endExcl: b.endExcl };
}

// Range picker: inclusive end date → exclusive next day.
export function rangeEndExcl(endDateIso) {
  if (!endDateIso) return endDateIso;
  const [y, m, d] = endDateIso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + 1);
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
}

export const RELATIVE_PRESETS = [
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'this_quarter', label: 'This quarter', fiscalLabel: 'This fiscal quarter' },
  { key: 'last_quarter', label: 'Last quarter', fiscalLabel: 'Last fiscal quarter' },
  { key: 'this_year_to_date', label: 'Year to date', fiscalLabel: 'Fiscal YTD' },
  { key: 'last_year', label: 'Last year', fiscalLabel: 'Last fiscal year' },
  { key: 'last_7_days', label: 'Last 7 days' },
  { key: 'last_30_days', label: 'Last 30 days' },
  { key: 'last_90_days', label: 'Last 90 days' },
];
